// frontend/src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Trophy,
  Phone,
  Clock,
  TrendingUp,
  CheckCircle,
  Circle,
  PlayCircle,
  X
} from 'lucide-react';
import api from '../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface DashboardData {
  user: any;
  stats: {
    progression_globale: number;
    modules_termines: number;
    modules_total: number;
    quiz_reussis: number;
    quiz_total: number;
    meilleur_score_quiz: number | null;
    score_moyen_simulations: number | null;
    temps_total_formation: number;
  };
  current_promotion: any;
  recent_modules: any[];
  recent_quiz_results: any[];
  recent_simulations: any[];
}

export default function Dashboard() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [incompletePromotions, setIncompletePromotions] = useState<any[]>([]);
  const [showPromotionsModal, setShowPromotionsModal] = useState(false);
  const [nbNonTerminees, setNbNonTerminees] = useState(0);
  const [myPromotions, setMyPromotions] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
    fetchIncompletePromotions();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/users/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIncompletePromotions = async () => {
    try {
      const promotionsRes = await api.get('/promotions/my-promotions');
      setMyPromotions(promotionsRes.data);
      const addStatus = async (promotion: any) => {
        try {
          const modulesRes = await api.get(`/modules/promotion/${promotion.id}`);
          const modules = modulesRes.data;
          const completedModules = modules.filter((m: any) => m.status === 'completed').length;
          const progression =
            modules.length > 0 ? (completedModules / modules.length) * 100 : 0;
          const isCompleted = completedModules === modules.length && modules.length > 0;
          return { ...promotion, progression, isCompleted };
        } catch (err) {
          console.error('Erreur lors du chargement des modules:', err);
          return { ...promotion, progression: 0, isCompleted: false };
        }
      };
      const promotionsWithStatus = await Promise.all(
        promotionsRes.data.map((p: any) => addStatus(p))
      );
      const notCompleted = promotionsWithStatus.filter(
        (p: any) => p.isCompleted === false
      );
      setNbNonTerminees(notCompleted.length);
      return notCompleted;
    } catch (error) {
      console.error('Erreur lors de la récupération des formations:', error);
      setNbNonTerminees(0);
      return [];
    }
  };

  const handleContinueClick = async () => {
    const notCompleted = await fetchIncompletePromotions();
    setIncompletePromotions(notCompleted);
    setShowPromotionsModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div>Erreur de chargement</div>;
  }

  const { stats, recent_modules, recent_quiz_results, recent_simulations } = dashboardData;

  const pieData = [
    { name: 'Terminé', value: stats.modules_termines },
    { name: 'Restant', value: stats.modules_total - stats.modules_termines }
  ];

  const COLORS = ['#004E89', '#E5E7EB'];

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Bienvenue, {user?.prenom} {user?.nom} !
        </h1>
        <p className="mt-2 text-gray-600">
          {nbNonTerminees > 0
            ? `Vous avez ${nbNonTerminees} formation(s) non terminée(s).`
            : 'Aucune formation en cours.'}
        </p>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Progression globale</p>
              <p className="text-2xl font-bold text-gray-900">{stats.progression_globale.toFixed(0)}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Modules terminés</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.modules_termines}/{stats.modules_total}
              </p>
            </div>
            <BookOpen className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Meilleur score quiz</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.meilleur_score_quiz ? `${stats.meilleur_score_quiz}%` : '-'}
              </p>
            </div>
            <Trophy className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Temps total</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.floor(stats.temps_total_formation / 60)}h {stats.temps_total_formation % 60}m
              </p>
            </div>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Graphique de progression et Actions rapides */}
      <div className="grid grid-cols-1 gap-6 lg:gap-8 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2">
        {/* Graphique circulaire */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Progression des modules</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Actions rapides</h2>
          {myPromotions.length > 0 && (
            <div className="space-y-3">
              <button
                onClick={handleContinueClick}
                className="w-full text-left flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <PlayCircle className="h-5 w-5 text-primary mr-3" />
                  <span className="font-medium">Continuer la formation</span>
                </div>
                <span className="text-sm text-gray-500">→</span>
              </button>

              {stats.modules_termines === stats.modules_total && (
                <Link
                  to="/quiz"
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <Trophy className="h-5 w-5 text-yellow-500 mr-3" />
                    <span className="font-medium">Passer le quiz</span>
                  </div>
                  <span className="text-sm text-gray-500">→</span>
                </Link>
              )}

              <Link
                to="/simulations"
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-green-500 mr-3" />
                  <span className="font-medium">Nouvelle simulation</span>
                </div>
                <span className="text-sm text-gray-500">→</span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Modules récents */}
      {recent_modules.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Modules récents</h2>
          <div className="space-y-3">
            {recent_modules.map((module) => (
              <div key={module.id} className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  {module.status === 'completed' ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  ) : module.status === 'in_progress' ? (
                    <Circle className="h-5 w-5 text-yellow-500 mr-3" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-300 mr-3" />
                  )}
                  <span className="font-medium">{module.titre}</span>
                </div>
                <span className="text-sm text-gray-500">
                  {module.time_spent > 0 && `${Math.floor(module.time_spent / 60)}m`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Résultats quiz récents */}
      {recent_quiz_results.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Résultats de quiz récents</h2>
          <div className="space-y-3">
            {recent_quiz_results.map((result) => (
              <div key={result.id} className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <Trophy className={`h-5 w-5 mr-3 ${result.passed ? 'text-green-500' : 'text-red-500'}`} />
                  <span className="font-medium">Quiz du {new Date(result.taken_at).toLocaleDateString()}</span>
                </div>
                <span className={`text-sm font-semibold ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {result.score_percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modale des promotions non terminées */}
      {showPromotionsModal && (
        <div
          className="modal-backdrop flex items-center justify-center"
          style={{ zIndex: 2147483647 }}
        >
          <div className="hidden"></div>

          <div className="relative z-10 flex min-h-full items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="promos-title"
              /* élargie légèrement + reste centrée */
              className="modal-container-lg relative w-[92vw] max-w-xl md:max-w-2xl"
            >
              <div className="flex items-center justify-between pb-4 border-b">
                <h3 id="promos-title" className="text-lg font-semibold">Formations non terminées</h3>
                <button
                  onClick={() => setShowPromotionsModal(false)}
                  className="rounded-full p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="pt-4">
                {incompletePromotions.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600">Toutes les formations sont terminées</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {incompletePromotions.map((promotion) => (
                      <div
                        key={promotion.id}
                        className="flex items-center justify-between p-4 border rounded-lg bg-white/80 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{promotion.nom}</h4>
                          {promotion.description && (
                            <p className="text-sm text-gray-600 mt-1">{promotion.description}</p>
                          )}
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-sm text-gray-500">
                              <span>Progression</span>
                              <span>{Math.round(promotion.progression)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-primary h-2 rounded-full"
                                style={{ width: `${promotion.progression}%` }}
                              />
                            </div>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-2">
                            Non terminée
                          </span>
                        </div>
                        <div className="ml-4">
                          <Link
                            to={`/promotions/${promotion.id}/modules`}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors"
                            onClick={() => setShowPromotionsModal(false)}
                          >
                            Continuer
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
